import { Delete } from "@mui/icons-material";
import { Button, IconButton, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { updateCategory } from "api/services/categories";
import DrawerWrapper from "components/DrawerWrapper";
import LoadingButton from "components/LoadingButton";
import UploadImage from "components/UploadImage";
import { snack } from "components/toast";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { DialogProps } from "types";
import { QuantumCategory } from "./QuantumCategoryCard";
import { updateQuantumCategory } from "api/services/quantumTemplate";

interface StateProps {
  name: string;
  subCategories: Array<{ name: string }>;
}

interface EditCategoryProps extends DialogProps {
  data: QuantumCategory;
}

function EditCategory({ open, setOpen, data }: EditCategoryProps) {
  const queryClient = useQueryClient();

  const [state, setState] = useState<StateProps>({
    name: "",
    subCategories: [],
  });
  const [subCategory, setSubCategory] = useState<string>("");

  useEffect(() => {
    setState({
      name: data.name,
      subCategories: data.subCategories,
    });
  }, [data]);

  const handleChange = (key: string, v: string) => {
    setState({ ...state, [key]: v });
  };

  const { mutate, isLoading } = useMutation(updateQuantumCategory, {
    onSuccess: () => {
      snack.success("Category Updated");
      setOpen(false);
      queryClient.invalidateQueries("quantum-categories");
    },
    onError: (err: any) => {
      snack.error(err.response.data.message);
    },
  });

  const addSubCategory = () => {
    if (!subCategory) return snack.error("Enter Subcategory");
    setState({
      ...state,
      subCategories: [...state.subCategories, { name: subCategory }],
    });
    setSubCategory("");
  };

  const deleteSubCategory = (index: any) => {
    let subCategories = [...state.subCategories];
    subCategories.splice(index, 1);
    setState({
      ...state,
      subCategories: subCategories,
    });
  };

  const handleSubmit = () => {
    if (!state.name) return snack.error("Name is required");

    let emptySubCategories = state.subCategories.some((sub) => sub.name === "");

    if (emptySubCategories) {
      return snack.error("Subcategory name is required");
    }

    mutate({
      id: data.id,
      data: state,
    });
  };

  return (
    <DrawerWrapper open={open} setOpen={setOpen} title="Edit Document Category">
      <TextField
        InputLabelProps={{ shrink: true }}
        variant="outlined"
        fullWidth
        value={state.name}
        onChange={(e) => handleChange("name", e.target.value)}
        size="small"
        label="Name"
      />
      <Box display="flex" gap={1} mt={3}>
        <TextField
          variant="outlined"
          fullWidth
          value={subCategory}
          onChange={(e) => setSubCategory(e.target.value)}
          size="small"
          label="Add Subcategory"
        />
        <Button
          onClick={addSubCategory}
          sx={{ minWidth: 80 }}
          variant="outlined"
          color="primary"
        >
          + Add
        </Button>
      </Box>
      <Box my={2}>
        {state.subCategories.map((item, index) => (
          <Box
            key={index}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
            gap={1}
          >
            <Typography variant="subtitle2">{index + 1}</Typography>
            <TextField
              value={item.name}
              onChange={(e) => {
                let subCategories = [...state.subCategories];
                subCategories[index].name = e.target.value;
                setState({ ...state, subCategories });
              }}
              size="small"
              sx={{ flex: 1 }}
              variant="outlined"
            />
            <IconButton size="small" onClick={() => deleteSubCategory(index)}>
              <Delete color="info" />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
        <LoadingButton
          onClick={handleSubmit}
          loading={isLoading}
          fullWidth
          loadingColor="white"
          title="Update"
          color="secondary"
        />
      </Box>
    </DrawerWrapper>
  );
}

export default EditCategory;
